import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

interface PatientConsultationWarningModalProps {
  open: boolean;
  title: string;
  message: string;
  onStartNew: () => Promise<void> | void;
  onStartPrevious: () => Promise<void> | void;
  onCancel: () => void;
}

export function PatientConsultationWarningModal({
  open,
  title,
  message,
  onStartNew,
  onStartPrevious,
  onCancel,
}: PatientConsultationWarningModalProps) {
  const [loadingNew, setLoadingNew] = useState(false);
  const [loadingPrev, setLoadingPrev] = useState(false);

  const handleStartNew = async () => {
    if (loadingNew || loadingPrev) return;
    setLoadingNew(true);
    try {
      await onStartNew();
    } finally {
      setLoadingNew(false);
    }
  };

  const handleStartPrevious = async () => {
    if (loadingPrev || loadingNew) return;
    setLoadingPrev(true);
    try {
      await onStartPrevious();
    } finally {
      setLoadingPrev(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onCancel}>
      <DialogContent className="max-w-xl p-6 sm:p-8 rounded-2xl">
        <DialogHeader>
          <DialogTitle className="text-lg sm:text-xl font-semibold">
            {title}
          </DialogTitle>
          <DialogDescription className="text-base text-gray-600 mt-2">
            {message}
          </DialogDescription>
        </DialogHeader>

        <DialogFooter className="flex flex-col sm:flex-row sm:justify-end gap-3 mt-6">
          <Button
            variant="secondary"
            onClick={handleStartPrevious}
            disabled={loadingPrev || loadingNew}
            className="w-full sm:w-auto text-sm sm:text-base py-2 sm:py-3"
          >
            {loadingPrev ? "Loading..." : "Continue Pending Consultation"}
          </Button>

          <Button
            variant="default"
            onClick={handleStartNew}
            disabled={loadingNew || loadingPrev}
            className="w-full sm:w-auto text-sm sm:text-base py-2 sm:py-3"
          >
            {loadingNew ? "Starting..." : "Start New Consultation"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
